import Button from 'components/ReusableComponents/Button'
import Checkbox from 'components/ReusableComponents/Checkbox'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import useCopyText from 'hooks/useCopyText'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useToasts } from 'react-toast-notifications'
import pmsApi from 'services/api/pms'
import { formatPhoneNumber } from 'utils/helpers'

import styles from './GXPLogin.module.scss'
import { useGXPLogin } from './GXPLoginContext'

export default function UserSetupStep() {
  const copy = useCopyText()
  const { addToast, removeToast } = useToasts()
  const { setActiveStep, data, pushToData, phoneNumber } = useGXPLogin()
  const { handleSubmit, control, setValue } = useForm({
    defaultValues: {
      username: '',
      password: '',
      ans1: '',
      ans2: '',
      ans3: '',
      requestAccessCheck: false
    }
  })
  const [loading, setLoading] = useState(false)

  const onSubmit = handleSubmit(
    async data => {
      setLoading(true)
      const res = await pmsApi.updateMarriotGxpConfig({
        userName: data.username,
        password: data.password,
        securityQuestions: `${data.ans1} --- ${data.ans2} --- ${data.ans3}`,
        activeStep: 'ReportsCreating'
      })
      pushToData(res)
      setLoading(false)
      setActiveStep('ReportsCreating')
    },
    errors => {
      const error =
        errors.username ||
        errors.password ||
        errors.ans1 ||
        errors.ans2 ||
        errors.ans3 ||
        errors.requestAccessCheck

      let errorText = ''
      const errRef = error.ref.name
      if (errRef === 'requestAccessCheck') {
        errorText = "You should accept you've requested regional/above property access"
      } else {
        const fieldName =
          errRef === 'username'
            ? 'User name'
            : errRef === 'password'
            ? 'Password'
            : 'Secret answers'
        errorText = `You must provide ${fieldName}`
      }

      addToast(errorText, {
        appearance: 'error',
        autoDismiss: true
      })
    }
  )

  const onGetOneTimePasswordClick = async () => {
    if (loading) return
    setLoading(true)
    const toastId = addToast('Getting password...', {
      appearance: 'info',
      autoDismiss: false
    })
    await pmsApi
      .getOneTimePassword({ phone: data.phoneNumber })
      .then(res => {
        copy(res)
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        removeToast(toastId)
        setLoading(false)
      })
  }

  return (
    <form onSubmit={onSubmit}>
      <div className={styles.list}>
        <div className={styles.listItem}>
          Login to{' '}
          <a
            className={styles.link}
            href="http://www.identityshoppe.marriott.com"
            target="_blank"
            rel="noreferrer"
          >
            Identity Shoppe
          </a>{' '}
          and reset to password
        </div>
        <div className={styles.listItem}>
          Setup security and dual factor authentication with this phone number:{' '}
          {formatPhoneNumber(phoneNumber ?? '5555555555')}
        </div>
        <div className={styles.listItem}>Set up security questions</div>
        <div className={styles.listItem}>Request regional/above property access</div>
        <div className={styles.listItem}>
          When asked,{' '}
          <span className={styles.link} onClick={onGetOneTimePasswordClick}>
            click here
          </span>{' '}
          to get one time password.
        </div>
      </div>

      <div className={styles.userPass}>
        <Controller
          control={control}
          name="username"
          render={({ field }) => (
            <TextInput
              label="User name"
              value={field.value}
              onChangeValue={v => setValue('username', v)}
            />
          )}
          rules={{ required: true }}
        />

        <Controller
          control={control}
          name="password"
          render={({ field }) => (
            <TextInput
              value={field.value}
              label="Password"
              type="password"
              onChangeValue={v => setValue('password', v)}
            />
          )}
          rules={{ required: true }}
        />
      </div>

      <p className={styles.regionalAccessTitle}>Challenge questions & anwers</p>

      <div className={styles.formQuestionAnswers}>
        <Controller
          control={control}
          name="ans1"
          render={({ field }) => (
            <TextInput
              valid={field.value}
              label="What was the name of your elementary or primary school?"
              onChangeValue={v => setValue('ans1', v)}
            />
          )}
          rules={{ required: true }}
        />

        <Controller
          control={control}
          name="ans2"
          render={({ field }) => (
            <TextInput
              valid={field.value}
              label="What was your first job?"
              onChangeValue={v => setValue('ans2', v)}
            />
          )}
          rules={{ required: true }}
        />

        <Controller
          control={control}
          name="ans3"
          render={({ field }) => (
            <TextInput
              value={field.value}
              label="What is your mother's middle name?"
              onChangeValue={v => setValue('ans3', v)}
            />
          )}
          rules={{ required: true }}
        />

        <Controller
          control={control}
          name="requestAccessCheck"
          render={({ field }) => (
            <label className={styles.checkRow}>
              <Checkbox value={field.value} onChange={field.onChange} />
              <span>I’ve requested regional/above property access</span>
            </label>
          )}
          rules={{ required: true }}
        />

        <div className={styles.actions}>
          <Button disabled={loading}>Next</Button>
        </div>
      </div>
    </form>
  )
}
